import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, parseISO, addWeeks, isBefore, isAfter } from "date-fns";
import { ArrowLeft, ChevronLeft, ChevronRight, Clock, Plus, X, Package, Phone, Mail, User, CalendarDays, Trash2, Ban, CheckCircle, Lock, Unlock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { type: "suits", name: "Custom Suit" },
  { type: "shirts", name: "Custom Shirt" },
  { type: "wedding", name: "Wedding Attire" },
  { type: "alterations", name: "Alterations" },
  { type: "pickup", name: "Ready for Pickup" },
];

const TIME_SLOTS = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM"
];

const AdminCalendarPage = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [turnaroundTimes, setTurnaroundTimes] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayAppointments, setDayAppointments] = useState([]);
  const [detailDialog, setDetailDialog] = useState(false);
  const [turnaroundWeeks, setTurnaroundWeeks] = useState("4");
  const [turnaroundNote, setTurnaroundNote] = useState("");
  const [showAddAppointment, setShowAddAppointment] = useState(false);
  const [readyForPickup, setReadyForPickup] = useState([]);
  const [editingArrival, setEditingArrival] = useState(null);
  const [newArrivalDate, setNewArrivalDate] = useState("");
  const [showPickupForm, setShowPickupForm] = useState(false);
  const [pickupServiceFilter, setPickupServiceFilter] = useState("all");
  const [pickupForm, setPickupForm] = useState({
    appointment_id: "",
    client_name: "",
    pickup_date: "",
    pickup_time: "10:00 AM",
    notes: ""
  });
  const [appointmentForm, setAppointmentForm] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    service_type: "suits",
    appointment_date: "",
    appointment_time: "10:00 AM",
    notes: ""
  });
  const [factoryTime, setFactoryTime] = useState(new Date());
  const navigate = useNavigate();

  // Blocked times state
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockForm, setBlockForm] = useState({
    date: "",
    time_slots: [],
    block_type: "blocked",
    reason: "",
    recurring: false,
    recurring_day: null
  });
  const [selectedBlockedSlots, setSelectedBlockedSlots] = useState([]);

  // Update factory time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setFactoryTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Get Qingdao, China time (UTC+8)
  const getFactoryTime = () => {
    return factoryTime.toLocaleString('en-US', {
      timeZone: 'Asia/Shanghai',
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  // Factory Days Off - Chinese Holidays (2025)
  const factoryClosures = [
    { start: new Date(2025, 0, 1), end: new Date(2025, 0, 3), label: "New Year's Day" },
    { start: new Date(2025, 1, 12), end: new Date(2025, 1, 24), label: "Spring Festival" },
    { start: new Date(2025, 3, 4), end: new Date(2025, 3, 6), label: "Qingming Festival" },
    { start: new Date(2025, 4, 1), end: new Date(2025, 4, 5), label: "Labor Day" },
    { start: new Date(2025, 5, 19), end: new Date(2025, 5, 21), label: "Dragon Boat Festival" },
    { start: new Date(2025, 8, 25), end: new Date(2025, 8, 27), label: "Mid-Autumn Festival" },
    { start: new Date(2025, 9, 1), end: new Date(2025, 9, 7), label: "National Day" }
  ];

  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [appointmentsRes, turnaroundRes, blockedRes] = await Promise.all([
        axios.get(`${API}/appointments`),
        axios.get(`${API}/turnaround-times`),
        axios.get(`${API}/blocked-times`)
      ]);
      setAppointments(appointmentsRes.data);
      setBlockedTimes(blockedRes.data || []);
      
      // Convert turnaround times array to object keyed by date
      const turnaroundMap = {};
      turnaroundRes.data.forEach(t => {
        turnaroundMap[t.date] = t;
      });
      setTurnaroundTimes(turnaroundMap);

      // Calculate ready for pickup (garments that have arrived - 4 weeks from appointment)
      const today = new Date();
      const ready = appointmentsRes.data.filter(apt => {
        if (!apt.appointment_date || apt.status === "cancelled") return false;
        const appointmentDate = parseISO(apt.appointment_date);
        // Get turnaround for that date, default to 4 weeks
        const dateStr = format(appointmentDate, "yyyy-MM-dd");
        const weeks = turnaroundMap[dateStr]?.weeks || 4;
        const arrivalDate = addWeeks(appointmentDate, weeks);
        // Ready if arrival date is today or in the past
        return isBefore(arrivalDate, today) || isSameDay(arrivalDate, today);
      });
      setReadyForPickup(ready);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Check if a date has any blocked times
  const getBlockedInfoForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const dayOfWeek = date.getDay() === 0 ? 6 : date.getDay() - 1; // Convert to 0=Monday
    
    const directBlocks = blockedTimes.filter(b => b.date === dateStr && !b.recurring);
    const recurringBlocks = blockedTimes.filter(b => b.recurring && b.recurring_day === dayOfWeek);
    
    const allBlocks = [...directBlocks, ...recurringBlocks];
    
    // Check if entire day is blocked (any block with empty time_slots)
    const isFullDayBlocked = allBlocks.some(b => !b.time_slots || b.time_slots.length === 0);
    const partiallyBlocked = allBlocks.some(b => b.time_slots && b.time_slots.length > 0);
    
    return { isFullDayBlocked, partiallyBlocked, blocks: allBlocks };
  };

  const isFactoryClosed = (date) => {
    return factoryClosures.some(closure => 
      date >= closure.start && date <= closure.end
    );
  };

  const getFactoryClosureLabel = (date) => {
    const closure = factoryClosures.find(c => date >= c.start && date <= c.end);
    return closure?.label || "Factory Closed";
  };

  const getAppointmentsForDate = (date) => {
    return appointments.filter(apt => {
      if (!apt.appointment_date) return false;
      const aptDate = parseISO(apt.appointment_date);
      return isSameDay(aptDate, date);
    });
  };

  const getTurnaroundForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return turnaroundTimes[dateStr]?.weeks || null;
  };

  const handleDateClick = (date) => {
    const dayApts = getAppointmentsForDate(date);
    const dateStr = format(date, "yyyy-MM-dd");
    const existingTurnaround = turnaroundTimes[dateStr];
    
    setSelectedDate(date);
    setDayAppointments(dayApts);
    setTurnaroundWeeks(existingTurnaround?.weeks?.toString() || "4");
    setTurnaroundNote(existingTurnaround?.note || "");
    setAppointmentForm(prev => ({ ...prev, appointment_date: dateStr }));
    setDetailDialog(true);
  };

  const getArrivalDate = (appointmentDate) => {
    const dateStr = format(appointmentDate, "yyyy-MM-dd");
    const weeks = turnaroundTimes[dateStr]?.weeks || 4;
    return addWeeks(appointmentDate, weeks);
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    if (!appointmentForm.client_name || !appointmentForm.client_email || !appointmentForm.appointment_date) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      const appointmentDate = parseISO(appointmentForm.appointment_date);
      const arrivalDate = getArrivalDate(appointmentDate);
      
      await axios.post(`${API}/appointments`, {
        ...appointmentForm,
        service_name: SERVICES.find(s => s.type === appointmentForm.service_type)?.name,
        estimated_arrival_date: format(arrivalDate, "yyyy-MM-dd"),
        status: "confirmed"
      });
      
      toast.success("Appointment created!");
      setShowAddAppointment(false);
      setAppointmentForm({
        client_name: "",
        client_email: "",
        client_phone: "",
        service_type: "suits",
        appointment_date: selectedDate ? format(selectedDate, "yyyy-MM-dd") : "",
        appointment_time: "10:00 AM",
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to create appointment");
    }
  };

  // Update estimated arrival date for an appointment
  const handleUpdateArrival = async (appointmentId) => {
    if (!newArrivalDate) {
      toast.error("Please select an arrival date");
      return;
    }
    
    try {
      await axios.put(`${API}/appointments/${appointmentId}`, {
        estimated_arrival_date: newArrivalDate
      });
      toast.success("Arrival date updated!");
      setEditingArrival(null);
      setNewArrivalDate("");
      fetchData();
      
      // Refresh day appointments
      if (selectedDate) {
        const dayApts = appointments.map(apt => 
          apt.id === appointmentId 
            ? { ...apt, estimated_arrival_date: newArrivalDate }
            : apt
        ).filter(apt => {
          if (!apt.appointment_date) return false;
          const aptDate = parseISO(apt.appointment_date);
          return isSameDay(aptDate, selectedDate);
        });
        setDayAppointments(dayApts);
      }
    } catch (error) {
      toast.error("Failed to update arrival date");
    }
  };

  // Schedule pickup appointment
  const handleSchedulePickup = async (e) => {
    e.preventDefault();
    if (!pickupForm.pickup_date || !pickupForm.pickup_time) {
      toast.error("Please select pickup date and time");
      return;
    }

    try {
      await axios.put(`${API}/appointments/${pickupForm.appointment_id}`, {
        pickup_date: pickupForm.pickup_date,
        pickup_time: pickupForm.pickup_time,
        pickup_notes: pickupForm.notes,
        status: "ready_for_pickup"
      });
      
      toast.success("Pickup scheduled!");
      setShowPickupForm(false);
      setPickupForm({
        appointment_id: "",
        client_name: "",
        pickup_date: "",
        pickup_time: "10:00 AM",
        notes: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to schedule pickup");
    }
  };

  const openPickupForm = (apt) => {
    // Default pickup date to 4 weeks after appointment (arrival date)
    const appointmentDate = parseISO(apt.appointment_date);
    const defaultArrival = apt.estimated_arrival_date 
      ? apt.estimated_arrival_date 
      : format(getArrivalDate(appointmentDate), "yyyy-MM-dd");
    
    setPickupForm({
      appointment_id: apt.id,
      client_name: apt.client_name,
      pickup_date: defaultArrival,
      pickup_time: "10:00 AM",
      notes: ""
    });
    setShowPickupForm(true);
  };

  // Cancel/Remove from Ready for Pickup
  const handleCancelPickup = async (apt) => {
    const action = apt.pickup_date ? "cancel the scheduled pickup" : "mark as picked up";
    if (!window.confirm(`Are you sure you want to ${action} for ${apt.client_name}?`)) return;
    
    try {
      await axios.put(`${API}/appointments/${apt.id}`, {
        status: "completed",
        pickup_date: null,
        pickup_time: null,
        pickup_notes: null
      });
      toast.success(apt.pickup_date ? "Pickup cancelled" : "Marked as picked up");
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  // Delete appointment
  const handleDeleteAppointment = async (apt) => {
    if (!window.confirm(`Are you sure you want to delete the appointment for ${apt.client_name}?`)) return;
    
    try {
      await axios.delete(`${API}/appointments/${apt.id}`);
      toast.success("Appointment deleted");
      
      // Update day appointments list
      setDayAppointments(prev => prev.filter(a => a.id !== apt.id));
      fetchData();
    } catch (error) {
      toast.error("Failed to delete appointment");
    }
  };

  // Block time functions
  const handleOpenBlockDialog = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    setBlockForm({
      date: dateStr,
      time_slots: [],
      block_type: "blocked",
      reason: "",
      recurring: false,
      recurring_day: null
    });
    setSelectedBlockedSlots([]);
    setShowBlockDialog(true);
  };

  const handleToggleBlockSlot = (slot) => {
    setSelectedBlockedSlots(prev => 
      prev.includes(slot) 
        ? prev.filter(s => s !== slot)
        : [...prev, slot]
    );
  };

  const handleBlockTime = async () => {
    try {
      const payload = {
        ...blockForm,
        time_slots: selectedBlockedSlots,
        recurring_day: blockForm.recurring ? (new Date(blockForm.date).getDay() === 0 ? 6 : new Date(blockForm.date).getDay() - 1) : null
      };
      
      await axios.post(`${API}/blocked-times`, payload);
      
      if (selectedBlockedSlots.length === 0) {
        toast.success(`${format(new Date(blockForm.date), "MMM d, yyyy")} has been blocked`);
      } else {
        toast.success(`Blocked ${selectedBlockedSlots.length} time slot(s)`);
      }
      
      setShowBlockDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to block time");
    }
  };

  const handleUnblockDate = async (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    
    if (!window.confirm(`Are you sure you want to open ${format(date, "MMM d, yyyy")} for appointments?`)) return;
    
    try {
      await axios.delete(`${API}/blocked-times/date/${dateStr}`);
      toast.success(`${format(date, "MMM d, yyyy")} is now open for appointments`);
      fetchData();
    } catch (error) {
      toast.error("Failed to unblock date");
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      await axios.delete(`${API}/blocked-times/${blockId}`);
      toast.success("Block removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove block");
    }
  };

  const saveTurnaroundTime = async () => {
    if (!selectedDate) return;
    
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    try {
      await axios.post(`${API}/turnaround-times`, {
        date: dateStr,
        weeks: parseInt(turnaroundWeeks),
        note: turnaroundNote
      });
      toast.success("Turnaround time saved");
      
      // Update local state
      setTurnaroundTimes(prev => ({
        ...prev,
        [dateStr]: { date: dateStr, weeks: parseInt(turnaroundWeeks), note: turnaroundNote }
      }));
    } catch (error) {
      toast.error("Failed to save turnaround time");
    }
  };

  const clearTurnaroundTime = async () => {
    if (!selectedDate) return;
    
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    try {
      await axios.delete(`${API}/turnaround-times/${dateStr}`);
      toast.success("Turnaround time cleared");
      
      // Update local state
      const newTimes = { ...turnaroundTimes };
      delete newTimes[dateStr];
      setTurnaroundTimes(newTimes);
      setTurnaroundWeeks("4");
      setTurnaroundNote("");
    } catch (error) {
      // Ignore if not found
    }
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOfWeek = monthStart.getDay();
  const emptyDays = Array(startDayOfWeek).fill(null);
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin")}
            className="mb-4"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Admin
          </Button>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="font-serif text-3xl tracking-tight">Appointment Calendar</h1>
              <p className="text-muted-foreground mt-2">View appointments, turnaround times & factory closures</p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              {/* Factory Time Display */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 px-4 py-2 rounded">
                <p className="text-xs text-yellow-500 font-medium">FACTORY TIME (Qingdao, China)</p>
                <p className="text-sm text-yellow-400 font-mono">{getFactoryTime()}</p>
              </div>
              
              <Button
                onClick={() => {
                  setAppointmentForm(prev => ({ ...prev, appointment_date: format(new Date(), "yyyy-MM-dd") }));
                  setShowAddAppointment(true);
                }}
                className="bg-primary text-primary-foreground hover:bg-foreground"
              >
                <Plus size={16} className="mr-2" />
                Add Appointment
              </Button>
            </div>
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap items-center gap-4 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-primary"></div>
              <span className="text-sm text-muted-foreground">Appointments</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
              <span className="text-sm text-muted-foreground">Custom Turnaround</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-sm text-muted-foreground">Factory Closed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm text-muted-foreground">Garment Arrival</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-slate-500"></div>
              <span className="text-sm text-muted-foreground">Day Blocked Off</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500"></div>
              <span className="text-sm text-muted-foreground">Partial Block</span>
            </div>
          </div>
        </div>
      </section>

      {/* Calendar */}
      <section className="py-8">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-white/10 p-6"
          >
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-8">
              <Button
                variant="ghost"
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              >
                <ChevronLeft size={20} />
              </Button>
              <h2 className="font-serif text-2xl">
                {format(currentMonth, "MMMM yyyy")}
              </h2>
              <Button
                variant="ghost"
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              >
                <ChevronRight size={20} />
              </Button>
            </div>

            {/* Week Days Header */}
            <div className="grid grid-cols-7 gap-2 mb-4">
              {weekDays.map(day => (
                <div key={day} className="text-center text-sm text-muted-foreground py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {emptyDays.map((_, index) => (
                <div key={`empty-${index}`} className="aspect-square"></div>
              ))}
              
              {monthDays.map(day => {
                const dayAppointments = getAppointmentsForDate(day);
                const isClosed = isFactoryClosed(day);
                const isToday = isSameDay(day, new Date());
                const hasAppointments = dayAppointments.length > 0;
                const turnaround = getTurnaroundForDate(day);
                const blockInfo = getBlockedInfoForDate(day);
                
                // Check if any garments arrive on this day
                const arrivingGarments = appointments.filter(apt => {
                  if (!apt.appointment_date || apt.status === "cancelled") return false;
                  const aptDate = parseISO(apt.appointment_date);
                  const dateStr = format(aptDate, "yyyy-MM-dd");
                  const weeks = turnaroundTimes[dateStr]?.weeks || 4;
                  const arrivalDate = addWeeks(aptDate, weeks);
                  return isSameDay(arrivalDate, day);
                });

                return (
                  <div
                    key={day.toISOString()}
                    onClick={() => handleDateClick(day)}
                    className={`
                      aspect-square border p-2 cursor-pointer transition-all relative
                      ${isToday ? "border-primary" : "border-white/10"}
                      ${isClosed ? "bg-red-500/10" : blockInfo.isFullDayBlocked ? "bg-slate-500/20" : blockInfo.partiallyBlocked ? "bg-orange-500/10" : "hover:border-primary/50"}
                      ${arrivingGarments.length > 0 && !blockInfo.isFullDayBlocked ? "bg-green-500/5" : ""}
                    `}
                  >
                    <div className={`text-sm ${isToday ? "text-primary font-bold" : ""} ${blockInfo.isFullDayBlocked ? "text-slate-400" : ""}`}>
                      {format(day, "d")}
                    </div>
                    
                    {/* Blocked day indicator */}
                    {blockInfo.isFullDayBlocked && (
                      <div className="absolute top-1 right-1">
                        <Ban size={12} className="text-slate-400" />
                      </div>
                    )}
                    
                    {/* Turnaround time display */}
                    {turnaround && !blockInfo.isFullDayBlocked && (
                      <div className="absolute top-1 right-1 text-[10px] text-yellow-500 font-medium">
                        {turnaround}w
                      </div>
                    )}
                    
                    {/* Indicators */}
                    <div className="absolute bottom-2 left-2 right-2 flex gap-1 flex-wrap">
                      {hasAppointments && (
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-primary"></div>
                          <span className="text-[10px] text-primary">{dayAppointments.length}</span>
                        </div>
                      )}
                      {arrivingGarments.length > 0 && (
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                          <span className="text-[10px] text-green-400">{arrivingGarments.length}</span>
                        </div>
                      )}
                      {turnaround && !hasAppointments && arrivingGarments.length === 0 && !blockInfo.isFullDayBlocked && (
                        <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                      )}
                      {isClosed && (
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      )}
                      {blockInfo.isFullDayBlocked && !isClosed && (
                        <div className="w-2 h-2 rounded-full bg-slate-500"></div>
                      )}
                      {blockInfo.partiallyBlocked && !blockInfo.isFullDayBlocked && (
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Ready for Pickup Section */}
          {readyForPickup.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 bg-green-500/5 border border-green-500/20 p-6 rounded"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <Package size={24} className="text-green-400" />
                  <div>
                    <h3 className="font-serif text-xl text-green-400">Ready for Pickup</h3>
                    <p className="text-sm text-muted-foreground">
                      {pickupServiceFilter === "all" 
                        ? `${readyForPickup.length} client(s) need to collect their garments`
                        : `${readyForPickup.filter(apt => apt.service_type === pickupServiceFilter).length} client(s) for ${SERVICES.find(s => s.type === pickupServiceFilter)?.name}`
                      }
                    </p>
                  </div>
                </div>
                
                {/* Service Filter Dropdown */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Filter:</span>
                  <select
                    value={pickupServiceFilter}
                    onChange={(e) => setPickupServiceFilter(e.target.value)}
                    className="bg-background border border-white/20 rounded-md px-3 py-1.5 text-sm"
                  >
                    <option value="all">All Services</option>
                    {SERVICES.filter(s => s.type !== "pickup").map(s => (
                      <option key={s.type} value={s.type}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid gap-3">
                {readyForPickup
                  .filter(apt => pickupServiceFilter === "all" || apt.service_type === pickupServiceFilter)
                  .map(apt => {
                  const appointmentDate = parseISO(apt.appointment_date);
                  const defaultArrival = getArrivalDate(appointmentDate);
                  const actualArrival = apt.estimated_arrival_date 
                    ? parseISO(apt.estimated_arrival_date) 
                    : defaultArrival;
                  
                  return (
                    <div key={apt.id} className="bg-card border border-white/10 p-4">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <User size={14} className="text-primary" />
                            <span className="font-medium">{apt.client_name}</span>
                            {apt.pickup_date && (
                              <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">
                                Pickup Scheduled
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{apt.service_name || apt.service_type}</p>
                          <p className="text-xs text-green-400 mt-1">
                            Arrived: {format(actualArrival, "MMM d, yyyy")}
                          </p>
                          {apt.pickup_date && (
                            <p className="text-xs text-blue-400 mt-1">
                              Pickup: {format(parseISO(apt.pickup_date), "MMM d, yyyy")} at {apt.pickup_time}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-col sm:items-end gap-1">
                            <a href={`tel:${apt.client_phone}`} className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1">
                              <Phone size={12} />
                              {apt.client_phone}
                            </a>
                            <a href={`mailto:${apt.client_email}`} className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1">
                              <Mail size={12} />
                              {apt.client_email}
                            </a>
                          </div>
                          <div className="flex gap-2 mt-2">
                            {!apt.pickup_date ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => openPickupForm(apt)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white"
                                >
                                  <CalendarDays size={14} className="mr-1" />
                                  Schedule Pickup
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleCancelPickup(apt)}
                                  className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                                >
                                  Picked Up
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCancelPickup(apt)}
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                              >
                                <X size={14} className="mr-1" />
                                Cancel Pickup
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Factory Days Off Notice */}
          <div className="mt-8 p-4 bg-red-500/10 border border-red-500/20 rounded">
            <h3 className="font-medium text-red-400 mb-3">Factory Days Off (2025 Chinese Holidays)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">New Year&apos;s Day</span>
                <span className="text-red-300">Jan 1–3</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Spring Festival</span>
                <span className="text-red-300">Feb 12–24</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Qingming Festival</span>
                <span className="text-red-300">Apr 4–6</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Labor Day</span>
                <span className="text-red-300">May 1–5</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Dragon Boat Festival</span>
                <span className="text-red-300">Jun 19–21</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Mid-Autumn Festival</span>
                <span className="text-red-300">Sep 25–27</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">National Day</span>
                <span className="text-red-300">Oct 1–7</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Day Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="bg-card border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              {selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-6">
            {/* Factory Closure Warning */}
            {selectedDate && isFactoryClosed(selectedDate) && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded">
                <p className="text-sm text-red-400 font-medium">⚠️ {getFactoryClosureLabel(selectedDate)}</p>
                <p className="text-xs text-muted-foreground">Factory is closed - extended production times apply</p>
              </div>
            )}

            {/* Block/Unblock Controls */}
            {selectedDate && (() => {
              const blockInfo = getBlockedInfoForDate(selectedDate);
              return (
                <div className={`p-4 rounded border ${blockInfo.isFullDayBlocked ? "bg-slate-500/10 border-slate-500/30" : blockInfo.partiallyBlocked ? "bg-orange-500/10 border-orange-500/30" : "bg-primary/5 border-primary/30"}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {blockInfo.isFullDayBlocked ? (
                        <>
                          <Lock size={16} className="text-slate-400" />
                          <h4 className="font-medium text-slate-400">Day Blocked Off</h4>
                        </>
                      ) : blockInfo.partiallyBlocked ? (
                        <>
                          <Ban size={16} className="text-orange-400" />
                          <h4 className="font-medium text-orange-400">Partial Block</h4>
                        </>
                      ) : (
                        <>
                          <Unlock size={16} className="text-primary" />
                          <h4 className="font-medium text-primary">Availability</h4>
                        </>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      {blockInfo.isFullDayBlocked ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUnblockDate(selectedDate)}
                          className="h-7 text-xs border-green-500/30 text-green-400 hover:bg-green-500/10"
                        >
                          <CheckCircle size={12} className="mr-1" />
                          Open Day
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleOpenBlockDialog(selectedDate)}
                          className="h-7 text-xs bg-slate-600 hover:bg-slate-700"
                        >
                          <Ban size={12} className="mr-1" />
                          Block Time
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {blockInfo.blocks.length > 0 && (
                    <div className="space-y-2 mt-2">
                      {blockInfo.blocks.map(block => (
                        <div key={block.id} className="flex items-center justify-between text-xs bg-background/50 p-2 rounded">
                          <div>
                            {block.time_slots && block.time_slots.length > 0 ? (
                              <span className="text-muted-foreground">
                                {block.time_slots.length} slot(s): {block.time_slots.slice(0, 3).join(", ")}{block.time_slots.length > 3 ? "..." : ""}
                              </span>
                            ) : (
                              <span className="text-slate-400">Entire day blocked</span>
                            )}
                            {block.reason && <span className="text-muted-foreground ml-2">({block.reason})</span>}
                            {block.recurring && <span className="text-amber-400 ml-2">[Weekly]</span>}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteBlock(block.id)}
                            className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                          >
                            <X size={12} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {!blockInfo.isFullDayBlocked && !blockInfo.partiallyBlocked && (
                    <p className="text-xs text-muted-foreground">This day is open for appointments</p>
                  )}
                </div>
              );
            })()}

            {/* Turnaround Time Setting */}
            <div className="p-4 bg-yellow-500/5 border border-yellow-500/20 rounded">
              <div className="flex items-center gap-2 mb-3">
                <Clock size={16} className="text-yellow-500" />
                <h4 className="font-medium text-yellow-500">Estimated Turnaround Time</h4>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Weeks</label>
                  <Select value={turnaroundWeeks} onValueChange={setTurnaroundWeeks}>
                    <SelectTrigger className="bg-background border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">2 weeks</SelectItem>
                      <SelectItem value="3">3 weeks</SelectItem>
                      <SelectItem value="4">4 weeks</SelectItem>
                      <SelectItem value="5">5 weeks</SelectItem>
                      <SelectItem value="6">6 weeks</SelectItem>
                      <SelectItem value="7">7 weeks</SelectItem>
                      <SelectItem value="8">8 weeks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Note (optional)</label>
                  <Input
                    placeholder="e.g. Holiday backlog"
                    value={turnaroundNote}
                    onChange={(e) => setTurnaroundNote(e.target.value)}
                    className="bg-background border-white/10"
                  />
                </div>
              </div>
              
              <div className="flex gap-2 mt-4">
                <Button
                  size="sm"
                  onClick={saveTurnaroundTime}
                  className="bg-yellow-600 text-white hover:bg-yellow-700"
                >
                  Save Turnaround
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={clearTurnaroundTime}
                  className="border-white/20"
                >
                  Clear
                </Button>
              </div>
            </div>
            
            {/* Appointments */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="mono-text text-primary text-xs">
                  {dayAppointments.length} Appointment(s)
                </h4>
                <Button
                  size="sm"
                  onClick={() => setShowAddAppointment(true)}
                  className="h-7 text-xs"
                >
                  <Plus size={12} className="mr-1" />
                  Add
                </Button>
              </div>
              
              {dayAppointments.length === 0 ? (
                <p className="text-muted-foreground text-sm">No appointments scheduled</p>
              ) : (
                <div className="space-y-3">
                  {dayAppointments.map(apt => {
                    const aptDate = parseISO(apt.appointment_date);
                    const defaultArrival = getArrivalDate(aptDate);
                    const actualArrival = apt.estimated_arrival_date 
                      ? parseISO(apt.estimated_arrival_date) 
                      : defaultArrival;
                    const isEditing = editingArrival === apt.id;
                    
                    return (
                      <div key={apt.id} className="border border-white/10 p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium">{apt.client_name}</h4>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded ${
                              apt.status === "cancelled" ? "bg-red-500/10 text-red-400" :
                              apt.status === "ready_for_pickup" ? "bg-blue-500/10 text-blue-400" :
                              apt.status === "confirmed" ? "bg-green-500/10 text-green-400" :
                              "bg-yellow-500/10 text-yellow-400"
                            }`}>
                              {apt.status === "ready_for_pickup" ? "Ready for Pickup" : apt.status}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteAppointment(apt)}
                              className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground">{apt.service_name || apt.service_type}</p>
                        <p className="text-sm text-muted-foreground">{apt.appointment_time}</p>
                        
                        {/* Editable Arrival Date */}
                        <div className="mt-3 p-2 bg-green-500/5 border border-green-500/20 rounded">
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <Input
                                type="date"
                                value={newArrivalDate}
                                onChange={(e) => setNewArrivalDate(e.target.value)}
                                className="bg-background border-white/10 h-8 text-sm flex-1"
                              />
                              <Button
                                size="sm"
                                onClick={() => handleUpdateArrival(apt.id)}
                                className="h-8 px-2"
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setEditingArrival(null); setNewArrivalDate(""); }}
                                className="h-8 px-2"
                              >
                                <X size={14} />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-green-400">
                                Est. Arrival: {format(actualArrival, "MMM d, yyyy")}
                              </p>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setEditingArrival(apt.id);
                                  setNewArrivalDate(format(actualArrival, "yyyy-MM-dd"));
                                }}
                                className="h-6 text-xs text-muted-foreground hover:text-primary"
                              >
                                Change
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Pickup Info */}
                        {apt.pickup_date && (
                          <div className="mt-2 p-2 bg-blue-500/10 border border-blue-500/20 rounded">
                            <p className="text-xs text-blue-400">
                              Pickup: {format(parseISO(apt.pickup_date), "MMM d, yyyy")} at {apt.pickup_time}
                            </p>
                          </div>
                        )}
                        
                        <p className="text-xs text-muted-foreground mt-2">{apt.client_email} • {apt.client_phone}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Appointment Modal */}
      <AnimatePresence>
        {showAddAppointment && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddAppointment(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-white/10 flex justify-between items-center sticky top-0 bg-card">
                <h2 className="font-serif text-xl">New Appointment</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAddAppointment(false)}
                >
                  <X size={18} />
                </Button>
              </div>

              <form onSubmit={handleCreateAppointment} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Client Name *</label>
                  <Input
                    value={appointmentForm.client_name}
                    onChange={(e) => setAppointmentForm({ ...appointmentForm, client_name: e.target.value })}
                    placeholder="Full name"
                    className="bg-background border-white/10"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Email *</label>
                    <Input
                      type="email"
                      value={appointmentForm.client_email}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, client_email: e.target.value })}
                      placeholder="email@example.com"
                      className="bg-background border-white/10"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Phone</label>
                    <Input
                      value={appointmentForm.client_phone}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, client_phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="bg-background border-white/10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Service</label>
                  <select
                    value={appointmentForm.service_type}
                    onChange={(e) => setAppointmentForm({ ...appointmentForm, service_type: e.target.value })}
                    className="w-full bg-background border border-white/10 rounded-md px-3 py-2"
                  >
                    {SERVICES.map(s => (
                      <option key={s.type} value={s.type}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Date *</label>
                    <Input
                      type="date"
                      value={appointmentForm.appointment_date}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, appointment_date: e.target.value })}
                      className="bg-background border-white/10"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Time</label>
                    <select
                      value={appointmentForm.appointment_time}
                      onChange={(e) => setAppointmentForm({ ...appointmentForm, appointment_time: e.target.value })}
                      className="w-full bg-background border border-white/10 rounded-md px-3 py-2"
                    >
                      {TIME_SLOTS.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Show estimated arrival */}
                {appointmentForm.appointment_date && (
                  <div className="p-3 bg-green-500/10 border border-green-500/20 rounded">
                    <p className="text-sm text-green-400">
                      <Package size={14} className="inline mr-2" />
                      Est. Garment Arrival: {format(
                        addWeeks(
                          parseISO(appointmentForm.appointment_date),
                          turnaroundTimes[appointmentForm.appointment_date]?.weeks || 4
                        ),
                        "MMMM d, yyyy"
                      )}
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Notes</label>
                  <Textarea
                    value={appointmentForm.notes}
                    onChange={(e) => setAppointmentForm({ ...appointmentForm, notes: e.target.value })}
                    placeholder="Any additional notes..."
                    className="bg-background border-white/10 min-h-[80px]"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddAppointment(false)}
                    className="flex-1 border-white/20"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-primary text-primary-foreground hover:bg-foreground"
                  >
                    Create Appointment
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Schedule Pickup Modal */}
      <AnimatePresence>
        {showPickupForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowPickupForm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-card border border-white/10 w-full max-w-md"
            >
              <div className="p-6 border-b border-white/10 flex justify-between items-center">
                <h2 className="font-serif text-xl">Schedule Pickup</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPickupForm(false)}
                >
                  <X size={18} />
                </Button>
              </div>

              <form onSubmit={handleSchedulePickup} className="p-6 space-y-4">
                <div className="p-3 bg-primary/10 border border-primary/20 rounded">
                  <p className="text-sm font-medium">{pickupForm.client_name}</p>
                  <p className="text-xs text-muted-foreground">Garments ready for collection</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Pickup Date *</label>
                    <Input
                      type="date"
                      value={pickupForm.pickup_date}
                      onChange={(e) => setPickupForm({ ...pickupForm, pickup_date: e.target.value })}
                      className="bg-background border-white/10"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-2">Pickup Time *</label>
                    <select
                      value={pickupForm.pickup_time}
                      onChange={(e) => setPickupForm({ ...pickupForm, pickup_time: e.target.value })}
                      className="w-full bg-background border border-white/10 rounded-md px-3 py-2"
                    >
                      {TIME_SLOTS.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-muted-foreground mb-2">Notes</label>
                  <Textarea
                    value={pickupForm.notes}
                    onChange={(e) => setPickupForm({ ...pickupForm, notes: e.target.value })}
                    placeholder="Any pickup instructions..."
                    className="bg-background border-white/10 min-h-[60px]"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowPickupForm(false)}
                    className="flex-1 border-white/20"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Schedule Pickup
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Block Time Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent className="bg-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">Block Appointment Time</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Block off time for {blockForm.date && format(new Date(blockForm.date), "MMMM d, yyyy")}
            </p>
            
            {/* Block Type */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Block Type</label>
              <Select value={blockForm.block_type} onValueChange={(v) => setBlockForm(prev => ({...prev, block_type: v}))}>
                <SelectTrigger className="bg-background border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="vacation">Vacation</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="holiday">Holiday</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Reason */}
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Reason (optional)</label>
              <Input
                placeholder="e.g. Doctor appointment"
                value={blockForm.reason}
                onChange={(e) => setBlockForm(prev => ({...prev, reason: e.target.value}))}
                className="bg-background border-white/10"
              />
            </div>
            
            {/* Recurring Option */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="recurring"
                checked={blockForm.recurring}
                onCheckedChange={(checked) => setBlockForm(prev => ({...prev, recurring: checked}))}
              />
              <label htmlFor="recurring" className="text-sm">
                Repeat every {blockForm.date && format(new Date(blockForm.date), "EEEE")}
              </label>
            </div>
            
            {/* Time Slots Selection */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-muted-foreground">Select specific times to block (or leave empty to block entire day)</label>
                {selectedBlockedSlots.length > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedBlockedSlots([])}
                    className="h-6 text-xs"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-4 gap-1 max-h-40 overflow-y-auto">
                {TIME_SLOTS.map(slot => (
                  <button
                    key={slot}
                    onClick={() => handleToggleBlockSlot(slot)}
                    className={`p-1.5 text-[10px] border rounded transition-all ${
                      selectedBlockedSlots.includes(slot)
                        ? "bg-red-500/20 border-red-500/50 text-red-400"
                        : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    {slot}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {selectedBlockedSlots.length === 0 
                  ? "⚠️ No slots selected = entire day will be blocked" 
                  : `${selectedBlockedSlots.length} slot(s) selected`}
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlockDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleBlockTime} className="bg-red-600 hover:bg-red-700">
              <Ban size={14} className="mr-1" />
              {selectedBlockedSlots.length === 0 ? "Block Entire Day" : `Block ${selectedBlockedSlots.length} Slot(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCalendarPage;
